/**
 * Shared Device ID Service (Kit19 SSO ecosystem).
 * All Kit19 apps in the same browser share the SAME device id under
 * localStorage 'kit19_device_id', so cross-app logout can target a device.
 * The SSO callback may also seed this key from the SSO redirect.
 *
 * PORTABLE: copy as-is, no per-project changes needed.
 */

// Storage key for the device ID (shared across all Kit19 apps)
const DEVICE_ID_KEY = 'kit19_device_id';

/**
 * Get or create a unique device ID for this browser.
 * @returns {string} Device ID (format: timestamp_random_userAgentHash)
 */
export const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
};

/**
 * Generate a unique device ID. Format: timestamp_randomString_userAgentHash
 * @returns {string} Unique device ID
 */
const generateDeviceId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);

  let userAgentHash;
  try {
    userAgentHash = btoa(navigator.userAgent).substring(0, 8);
  } catch {
    userAgentHash = 'unknown';
  }

  return `${timestamp}_${random}_${userAgentHash}`;
};

/**
 * Get the current device ID without creating one.
 * @returns {string|null} Device ID or null
 */
export const getDeviceIdIfExists = () => {
  return localStorage.getItem(DEVICE_ID_KEY);
};

/**
 * Clear the device ID (for logout/testing).
 */
export const clearDeviceId = () => {
  localStorage.removeItem(DEVICE_ID_KEY);
};

const deviceIdServiceExport = {
  getOrCreateDeviceId,
  getDeviceIdIfExists,
  clearDeviceId
};

export default deviceIdServiceExport;
